// Version Display Component for Helix 2000 MMORPG
// Shows current game version and build information

import React from 'react';
import { VERSION_INFO, getVersionDisplay, getGameTitle } from '../config/version/version';

interface VersionDisplayProps {
  style?: React.CSSProperties;
  showFull?: boolean;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  className?: string;
}

export const VersionDisplay: React.FC<VersionDisplayProps> = ({
  style,
  showFull = false,
  position = 'bottom-right',
  className = ''
}) => {
  const baseStyle: React.CSSProperties = {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'monospace',
    padding: '4px 8px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    zIndex: 1000,
    pointerEvents: 'none',
    userSelect: 'none',
    ...style
  };

  const getPositionStyle = (): React.CSSProperties => {
    switch (position) {
    case 'top-left':
      return { position: 'fixed', top: '10px', left: '10px' };
    case 'top-right':
      return { position: 'fixed', top: '10px', right: '10px' };
    case 'bottom-left':
      return { position: 'fixed', bottom: '10px', left: '10px' };
    case 'bottom-right':
    default:
      return { position: 'fixed', bottom: '10px', right: '10px' };
    }
  };

  const finalStyle = {
    ...baseStyle,
    ...getPositionStyle()
  };

  const versionText = showFull
    ? `${getGameTitle()} (${VERSION_INFO.buildTime})`
    : `${getVersionDisplay()}`;

  // Enhanced tooltip with more information
  const getTooltipText = () => {
    try {
      // Try to get git commit info from GAME_VERSION
      const gameVersion = require('../config/version').GAME_VERSION;
      return `Full Version: ${VERSION_INFO.fullVersion}\nEnvironment: ${VERSION_INFO.environment}\nBuild Time: ${VERSION_INFO.buildTime}\nGit Commit: ${gameVersion.commitHash ? gameVersion.commitHash.slice(0, 8) : 'N/A'}\nLast Updated: ${new Date().toLocaleString()}`;
    } catch {
      return `Full Version: ${VERSION_INFO.fullVersion}\nEnvironment: ${VERSION_INFO.environment}\nBuild Time: ${VERSION_INFO.buildTime}`;
    }
  };

  return (
    <div
      style={finalStyle}
      className={`version-display ${className}`}
      title={getTooltipText()}
    >
      🎮 {versionText}
    </div>
  );
};

// Simple version badge for inline usage
export const VersionBadge: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <span
    style={{
      fontSize: '10px',
      color: '#27ae60',
      backgroundColor: 'rgba(39, 174, 96, 0.1)',
      padding: '2px 6px',
      borderRadius: '3px',
      border: '1px solid rgba(39, 174, 96, 0.3)',
      fontFamily: 'monospace',
      ...style
    }}
    title={`Version ${VERSION_INFO.fullVersion}`}
  >
    v{getVersionDisplay()}
  </span>
);